import { createContext, useContext } from 'react'

export type Role = 'admin' | 'medico' | 'tens' | 'digitador' | 'recepcionista'

export interface SessionUser {
  id: string
  email: string
  role: Role
  /** True si este JWT pasó MFA challenge (session-specific). */
  mfaVerified: boolean
  /** True si el user TIENE MFA configurado activo (pending_verify=false). F3 AC-01. */
  mfaEnabled: boolean
}

export interface LoginResponse {
  token?: string
  requiresMfa: boolean
  mfaChallenge?: string
}

export interface MfaVerifyResponse {
  token: string
}

export interface AuthContextValue {
  user: SessionUser | null
  status: 'loading' | 'authenticated' | 'unauthenticated'
  error: string | null
  login: (email: string, password: string) => Promise<LoginResponse>
  verifyMfa: (mfaChallenge: string, code: string) => Promise<void>
  logout: () => void
  refresh: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

/**
 * NAV_BY_ROLE — autorización de RUTAS por rol (gate ProtectedRoute requireNav).
 *
 * Contiene TODOS los navIds permitidos al rol, incluyendo:
 *  - Los 4 módulos consolidados F7.5 (nav-dashboard, nav-expediente, nav-pdfs-upload, nav-admin*)
 *  - Los navIds legacy (nav-trabajadores, nav-fichas, etc.) que siguen accesibles
 *    vía URL durante migración (deep-links externos, redirects).
 *
 * El SIDEBAR solo muestra los 4 consolidados — ver `SIDEBAR_BY_ROLE` abajo.
 */
export const NAV_BY_ROLE: Record<Role, ReadonlyArray<string>> = {
  admin: [
    // F7.5 consolidados
    'nav-dashboard',
    'nav-expediente',
    'nav-pdfs-upload',
    'nav-admin',
    'nav-admin-usuarios',
    'nav-admin-empresas',
    'nav-admin-operativos',
    'nav-admin-api-keys',
    'nav-admin-scoring-rules',
    // Legacy (acceso vía URL durante migración)
    'nav-trabajadores',
    'nav-fichas',
    'nav-reports',
    'nav-settings',
    'nav-export',
    'nav-notifications',
    'nav-rescue',
    'nav-scoring-rules',
    'nav-api-keys',
    'nav-formularios',
  ],
  medico: [
    'nav-dashboard',
    'nav-expediente',
    'nav-pdfs-upload',
    // Legacy
    'nav-trabajadores',
    'nav-fichas',
    'nav-reports',
    'nav-export',
    'nav-rescue',
    'nav-formularios',
  ],
  tens: [
    'nav-expediente',
    'nav-pdfs-upload',
    // Legacy
    'nav-trabajadores',
    'nav-fichas',
    'nav-reports',
    'nav-formularios',
  ],
  digitador: [
    'nav-expediente',
    'nav-pdfs-upload',
    // Legacy
    'nav-trabajadores',
    'nav-fichas',
  ],
  recepcionista: [
    'nav-expediente',
    // Legacy
    'nav-trabajadores',
    'nav-notifications',
  ],
}

/**
 * SIDEBAR_BY_ROLE — qué entradas ve cada rol en el sidebar (F7.5 ADR-032).
 *
 * Contrato:
 *  - admin: 4 entries top-level (Dashboard, Expediente, Cargar PDF, Admin)
 *  - medico/tens: 3 entries (Dashboard, Expediente, Cargar PDF) | tens sin Dashboard
 *  - digitador: 2 entries (Expediente, Cargar PDF)
 *  - recepcionista: 1 entry (Expediente)
 *
 * IDs aquí DEBEN estar también en NAV_BY_ROLE para que ProtectedRoute permita acceso.
 */
export interface SidebarItem {
  navId: string
  children?: ReadonlyArray<string>
}

export const SIDEBAR_BY_ROLE: Record<Role, ReadonlyArray<SidebarItem>> = {
  admin: [
    { navId: 'nav-dashboard' },
    { navId: 'nav-expediente' },
    { navId: 'nav-pdfs-upload' },
    {
      navId: 'nav-admin',
      children: [
        'nav-admin-usuarios',
        'nav-admin-empresas',
        'nav-admin-operativos',
        'nav-admin-api-keys',
        'nav-admin-scoring-rules',
      ],
    },
  ],
  medico: [{ navId: 'nav-dashboard' }, { navId: 'nav-expediente' }, { navId: 'nav-pdfs-upload' }],
  tens: [{ navId: 'nav-expediente' }, { navId: 'nav-pdfs-upload' }],
  digitador: [{ navId: 'nav-expediente' }, { navId: 'nav-pdfs-upload' }],
  recepcionista: [{ navId: 'nav-expediente' }],
}

export function canAccess(role: Role, navId: string): boolean {
  return NAV_BY_ROLE[role].includes(navId)
}

export function defaultPathForRole(role: Role): string {
  // F7.5: todos los roles aterrizan en Expediente (corazón patient-centric).
  switch (role) {
    case 'admin':
    case 'medico':
      return '/app/dashboard'
    default:
      return '/app/expediente'
  }
}
